import { createAppSlice } from '.';
import { getWorksites as getWorksitesApi } from '../api/api';
import { TaskId } from '../api/queries/workOrders';
import { EquipmentModel } from '../constants/equipmentConstants';
import { DeviceSessionStatus } from '../constants/reportConstants';
import { WorksiteTaskStatus } from '../constants/worksiteConstants';
import { ItemWithPermissions } from '../utils/permissions';
import { getWorksiteKey } from '../utils/worksites';
import { ClusterId } from './clusters';
import { Equipment, EquipmentId } from './equipments';
import { ParcelId } from './parcels';
import { DeviceSessionId, EquipmentSession } from './sessions';
import { alerting } from './snacks';
import { userLoggedOut } from './users';

export type WorksiteKey = `${EquipmentId}-${ClusterId}-${DeviceSessionId}`;

type WorkedParcelWithTask = {
  parcel_id: ParcelId;
  parcel_name: string;
  parcel_area: number;
  parcel_category: string;
  worked_area: number;
  task_ids: TaskId[];
};

export type Worksite = ItemWithPermissions & {
  cluster_id: ClusterId;
  cluster_name: string;
  total_duration: number;
  total_in_parcels_duration: number;
  total_out_of_parcels_duration: number;
  total_motor_duration: number;
  total_movement_duration: number;
  total_in_parcels_motor_duration: number;
  total_in_farmhouses_motor_duration: number;
  total_out_of_parcels_motor_duration: number;
  total_distance: number;
  total_in_parcels_distance: number;
  total_out_of_parcels_distance: number;
  total_area: number;
  total_unit: number;
  total_fuel: number | null;
  total_in_parcels_fuel: number | null;
  total_out_of_parcels_fuel: number | null;
  sessions: EquipmentSession[];
  equipment_model: EquipmentModel;
  equipment_instances: Equipment[];
  device_session_id: DeviceSessionId;
  from_date: string;
  to_date: string;
  device_session_status: DeviceSessionStatus;
  task_status: WorksiteTaskStatus;
  worked_parcel_with_tasks: WorkedParcelWithTask[];
};

type State = {
  worksitesByKey: Record<WorksiteKey, Worksite>;
  worksitesDeviceSessionKeys: WorksiteKey[];
};

const initialState: State = {
  worksitesByKey: {},
  worksitesDeviceSessionKeys: [],
};

const worksitesSlice = createAppSlice({
  name: 'worksites',
  initialState,
  reducers: (create) => ({
    fetchWorksites: create.asyncThunk(
      async (params: string, { signal, dispatch }) => {
        return (await alerting(() => getWorksitesApi(params, signal), { dispatch })) as Worksite[];
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload?.length) {
            payload.forEach((worksite) => {
              const key = getWorksiteKey(worksite);
              if (key === 'error') {
                return;
              }
              if (!state.worksitesDeviceSessionKeys.includes(key)) {
                state.worksitesDeviceSessionKeys.push(key);
              }
              state.worksitesByKey[key] = worksite;
            });
          }
        },
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addCase(userLoggedOut, () => initialState);
  },
  selectors: {
    selectWorksitesByKey: (state) => state.worksitesByKey,
    selectWorksitesDeviceSessionKeys: (state) => state.worksitesDeviceSessionKeys,
  },
});

export const { fetchWorksites } = worksitesSlice.actions;
export const { selectWorksitesByKey, selectWorksitesDeviceSessionKeys } = worksitesSlice.selectors;

export default worksitesSlice.reducer;
